"use client";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Menu } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { SERVICE_QUEUE_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveServiceQueue from "@/hooks/requests/service-queue/use-remove-service-queue";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import IServiceQueue from "@/interfaces/service-queue.interface";
import { ServiceQueueMessages } from "@/messages/service-queue/service-queue.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: ServiceQueueMessages;
  selected: IServiceQueue;
}

export function ServiceQueuePopover({ isOpen, anchorEl, onClose, messages, selected }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { isVisible, handleOpen, handleClose } = useDialog<IServiceQueue>();

  const { remove, isLoading: isLoadingRemove } = useRemoveServiceQueue();
  const { mutate } = useMutate();

  const hasNoPermissionToEdit = useHasNoPermission(SERVICE_QUEUE_PERMISSIONS.EDIT);
  const hasNoPermissionToDelete = useHasNoPermission(SERVICE_QUEUE_PERMISSIONS.DELETE);

  const handleOnView = () => router(ROUTE_URLS.viewServiceQueue(lang, selected.id));
  const handleOnEdit = () => router(ROUTE_URLS.updateServiceQueue(lang, selected.id));

  const handleConfirmDelete = async (serviceQueue: IServiceQueue) => {
    const _id = serviceQueue.id;
    const onSuccess = () => {
      mutate(SWR_KEYS.getServiceQueues);
      handleClose();
      onClose();
    };
    const successMessage = messages.deleteServiceQueueSuccess;
    const errorMessage = messages.deleteServiceQueueError;
    const onError = () => handleClose();

    await remove({ _id, onSuccess, successMessage, errorMessage, onError });
  };

  return (
    <Menu open={isOpen} anchorEl={anchorEl} onClose={onClose}>
      <NotificationItem
        icon={<VisibilityIcon />}
        onClick={handleOnView}
        title={messages.viewPopover}
      />
      <NotificationItem
        icon={<EditIcon />}
        disabled={hasNoPermissionToEdit}
        onClick={handleOnEdit}
        title={messages.updatePopover}
      />
      <NotificationItem
        icon={<DeleteIcon />}
        disabled={hasNoPermissionToDelete}
        onClick={() => handleOpen(selected)}
        title={messages.removePopover}
      />

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={() => handleConfirmDelete(selected)}
        titleText={messages.deleteTitle}
        descriptionText={messages.deleteContent}
        cancelText={messages.deleteCancel}
        confirmText={messages.deleteConfirm}
        isLoading={isLoadingRemove}
      />
    </Menu>
  );
}
